class FeaturedCollectionsMegaMenu {
  constructor() {
    this.swipers = new Map();
    this.init();
  }

  init() {
    this.bindTabEvents();
    this.bindMegaMenuEvents();
    this.initializeFirstTab();
    this.observeMegaMenus();
  }

  bindTabEvents() {
    const tabs = document.querySelectorAll('.featured-collections-tab');

    tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchTab(tab);
      });
    });
  }

  switchTab(activeTab) {
    const megaMenu = activeTab.closest('.featured-collections-mega-menu');
    const tabs = megaMenu.querySelectorAll('.featured-collections-tab');
    const panels = megaMenu.querySelectorAll('.featured-collections-panel');

    // Remove active class from all tabs
    tabs.forEach((tab) => {
      tab.classList.remove('active');
      tab.setAttribute('aria-selected', 'false');
    });

    // Hide all panels
    panels.forEach((panel) => {
      panel.classList.add('hidden');
    });

    // Activate clicked tab
    activeTab.classList.add('active');
    activeTab.setAttribute('aria-selected', 'true');

    // Get the collection handle and update mega-menu__content class
    const collectionHandle = activeTab.getAttribute('data-collection-handle');
    const megaMenuContent = activeTab.closest('.mega-menu__content');

    if (megaMenuContent && collectionHandle) {
      // Remove any existing collection classes
      const existingClasses = Array.from(megaMenuContent.classList).filter((cls) => cls.startsWith('collection-'));
      existingClasses.forEach((cls) => megaMenuContent.classList.remove(cls));

      // Add new collection class
      megaMenuContent.classList.add(`collection-${collectionHandle}`);
    }

    // Show corresponding panel
    const targetPanel = document.getElementById(activeTab.getAttribute('aria-controls'));
    if (targetPanel) {
      targetPanel.classList.remove('hidden');
      // Initialize or update swiper for this panel
      this.initializeSwiper(targetPanel);
    }
  }

  bindMegaMenuEvents() {
    // Listen for mega menu open events
    document.addEventListener('click', (e) => {
      const menuItem = e.target.closest('.header__menu-item');
      if (menuItem) {
        // Check if this menu item has a featured collections mega menu
        const megaMenuContent = document.querySelector(`#MegaMenu-Content-${menuItem.id.split('-')[1]}`);
        if (megaMenuContent && megaMenuContent.querySelector('.featured-collections-mega-menu')) {
          // Small delay to ensure the mega menu is fully opened
          setTimeout(() => {
            this.initializeFirstTabForMegaMenu(megaMenuContent);
          }, 100);
        }
      }
    });

    // Also listen for hover events on menu items
    document.addEventListener(
      'mouseenter',
      (e) => {
        const menuItem = e.target.closest('.header__menu-item');
        if (menuItem) {
          const megaMenuContent = document.querySelector(`#MegaMenu-Content-${menuItem.id.split('-')[1]}`);
          if (megaMenuContent && megaMenuContent.querySelector('.featured-collections-mega-menu')) {
            setTimeout(() => {
              this.initializeFirstTabForMegaMenu(megaMenuContent);
            }, 50);
          }
        }
      },
      true
    );
  }

  initializeFirstTabForMegaMenu(megaMenuContent) {
    const megaMenu = megaMenuContent.querySelector('.featured-collections-mega-menu');
    if (!megaMenu) return;

    const firstTab = megaMenu.querySelector('.featured-collections-tab');
    if (firstTab) {
      // Check if first tab is already active
      if (!firstTab.classList.contains('active')) {
        // Ensure the first tab is active
        this.switchTab(firstTab);
      } else {
        // If first tab is already active, just initialize its swiper
        const targetPanel = document.getElementById(firstTab.getAttribute('aria-controls'));
        if (targetPanel) {
          this.initializeSwiper(targetPanel);
        }
      }
    }
  }

  observeMegaMenus() {
    // Use Intersection Observer to detect when mega menus become visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const megaMenu = entry.target.querySelector('.featured-collections-mega-menu');
            if (megaMenu) {
              // Small delay to ensure the mega menu is fully rendered
              setTimeout(() => {
                this.initializeFirstTabForMegaMenu(entry.target);
              }, 50);
            }
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    // Observe all mega menu content containers
    const megaMenuContents = document.querySelectorAll('[id^="MegaMenu-Content-"]');
    megaMenuContents.forEach((content) => {
      observer.observe(content);
    });
  }

  initializeFirstTab() {
    // Find all mega menus with featured collections
    const megaMenus = document.querySelectorAll('.featured-collections-mega-menu');

    megaMenus.forEach((megaMenu) => {
      const firstTab = megaMenu.querySelector('.featured-collections-tab');
      if (firstTab) {
        // Check if first tab is already active
        if (!firstTab.classList.contains('active')) {
          // Ensure the first tab is active
          this.switchTab(firstTab);
        } else {
          // If first tab is already active, just initialize its swiper
          const targetPanel = document.getElementById(firstTab.getAttribute('aria-controls'));
          if (targetPanel) {
            this.initializeSwiper(targetPanel);
          }
        }
      }
    });
  }

  initializeSwiper(panel) {
    const swiperElement = panel.querySelector('.featured-collections-slider.swiper');
    if (!swiperElement) return;

    const collectionHandle = swiperElement.getAttribute('data-collection');

    // Destroy existing swiper if it exists
    if (this.swipers.has(collectionHandle)) {
      this.swipers.get(collectionHandle).destroy(true, true);
      this.swipers.delete(collectionHandle);
    }

    // Get products per slide from the swiper element or default to 4
    const productsPerSlide = this.getProductsPerSlide(swiperElement);

    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper.js is not loaded. Please ensure Swiper.js is included.');
      return;
    }

    // Initialize new swiper with enhanced configuration
    const swiper = new Swiper(swiperElement, {
      // Core settings
      slidesPerView: productsPerSlide,
      spaceBetween: 16,
      loop: false,
      grabCursor: true,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,

      // Navigation
      navigation: {
        nextEl: swiperElement.querySelector('.swiper-button-next'),
        prevEl: swiperElement.querySelector('.swiper-button-prev'),
        disabledClass: 'swiper-button-disabled',
      },

      // Pagination
      pagination: {
        el: swiperElement.querySelector('.swiper-pagination'),
        type: 'progressbar',
        clickable: false,
      },
      mousewheel: true,
      // Responsive breakpoints
      breakpoints: {
        320: {
          slidesPerView: 1,
          spaceBetween: 16,
        },
        640: {
          slidesPerView: 2,
          spaceBetween: 16,
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: productsPerSlide,
          spaceBetween: 16,
        },
      },

      // Event handlers
      on: {
        init: () => {
          // Ensure proper height for all slides
          this.equalizeSlideHeights(swiperElement);
          // Update navigation button states
          this.updateNavigationButtons(swiper);
        },
        slideChange: () => {
          // Update slide heights on change
          this.equalizeSlideHeights(swiperElement);
          // Update navigation button states
          this.updateNavigationButtons(swiper);
        },
        resize: () => {
          // Handle resize events
          this.equalizeSlideHeights(swiperElement);
        },
      },

      // Performance optimizations
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
    });

    // Store swiper instance
    this.swipers.set(collectionHandle, swiper);
  }

  getProductsPerSlide(swiperElement) {
    // Try to get from data attribute or default to 4
    const productsPerSlide = swiperElement.getAttribute('data-products-per-slide');
    return productsPerSlide ? parseInt(productsPerSlide) : 4;
  }

  equalizeSlideHeights(swiperElement) {
    const slides = swiperElement.querySelectorAll('.swiper-slide');
    if (slides.length === 0) return;

    // Reset heights to auto for fully responsive behavior
    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });

    // No height equalization - let slides be naturally adaptive to screen size
  }

  updateNavigationButtons(swiper) {
    if (!swiper) return;

    const nextButton = swiper.navigation?.nextEl;
    const prevButton = swiper.navigation?.prevEl;

    if (nextButton && prevButton) {
      // Update button states based on swiper position
      if (swiper.isEnd) {
        nextButton.classList.add('swiper-button-disabled');
        nextButton.setAttribute('aria-disabled', 'true');
      } else {
        nextButton.classList.remove('swiper-button-disabled');
        nextButton.setAttribute('aria-disabled', 'false');
      }

      if (swiper.isBeginning) {
        prevButton.classList.add('swiper-button-disabled');
        prevButton.setAttribute('aria-disabled', 'true');
      } else {
        prevButton.classList.remove('swiper-button-disabled');
        prevButton.setAttribute('aria-disabled', 'false');
      }
    }
  }

  destroyAllSwipers() {
    this.swipers.forEach((swiper) => {
      swiper.destroy(true, true);
    });
    this.swipers.clear();
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Check if Swiper is available before initializing
  if (typeof Swiper !== 'undefined') {
    window.featuredCollectionsMegaMenu = new FeaturedCollectionsMegaMenu();
  } else {
    console.warn('Swiper.js is not loaded. Featured collections mega menu will not function properly.');
  }
});

// Re-initialize when mega menu content is dynamically loaded
document.addEventListener('shopify:section:load', () => {
  if (typeof Swiper !== 'undefined') {
    // Clean up existing instance if it exists
    if (window.featuredCollectionsMegaMenu) {
      window.featuredCollectionsMegaMenu.destroyAllSwipers();
    }
    window.featuredCollectionsMegaMenu = new FeaturedCollectionsMegaMenu();
  }
});

// Clean up on page unload
window.addEventListener('beforeunload', () => {
  if (window.featuredCollectionsMegaMenu) {
    window.featuredCollectionsMegaMenu.destroyAllSwipers();
  }
});

// Handle window resize events for better responsive behavior
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    if (window.featuredCollectionsMegaMenu) {
      // Re-initialize swipers on resize to ensure proper responsive behavior
      window.featuredCollectionsMegaMenu.swipers.forEach((swiper) => {
        if (swiper && typeof swiper.update === 'function') {
          swiper.update();
        }
      });
    }
  }, 250);
});
